import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Cropper from 'react-easy-crop';
import IconButton from '@mui/material/IconButton';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import Slider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';

import 'react-easy-crop/react-easy-crop.css';

import {
  BUSINESS_LOGO_CROP_ASPECT,
  BUSINESS_LOGO_DISPLAY_HINT,
} from '../../constants/businessLogoDisplay';
import { getCroppedLogoBlob } from '../../utils/businessLogoCrop';

import {
  StyledCropDialog,
  CropModalTitle,
  CropModalContent,
  CropModalActions,
  CropStage,
  HintText,
  ZoomRow,
  PreviewRow,
  PreviewThumb,
  PreviewCaption,
  SecondaryModalButton,
  PrimaryModalButton,
} from './BusinessLogoCropModal.styled';

function BusinessLogoCropModal({
  open,
  imageSrc,
  onClose,
  onCroppedConfirm,
  isSubmitting,
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const previewDebounceRef = useRef(null);

  useEffect(() => {
    if (open && imageSrc) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    }
  }, [open, imageSrc]);

  useEffect(() => {
    return () => {
      if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const schedulePreview = useCallback(
    (pixels) => {
      if (!imageSrc || !pixels) return;
      if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
      previewDebounceRef.current = setTimeout(async () => {
        try {
          const blob = await getCroppedLogoBlob(imageSrc, pixels);
          const next = URL.createObjectURL(blob);
          setPreviewUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return next;
          });
        } catch {
          /* preview optional */
        }
      }, 140);
    },
    [imageSrc],
  );

  const onCropComplete = useCallback(
    (_area, pixels) => {
      setCroppedAreaPixels(pixels);
      schedulePreview(pixels);
    },
    [schedulePreview],
  );

  const handleSave = async () => {
    if (!imageSrc || !croppedAreaPixels || isSubmitting) return;
    const blob = await getCroppedLogoBlob(imageSrc, croppedAreaPixels);
    await onCroppedConfirm(blob);
  };

  const canSave = Boolean(croppedAreaPixels && imageSrc && !isSubmitting);

  const handleDialogClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  return (
    <StyledCropDialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <CropModalTitle>
        Frame your logo
        <IconButton
          aria-label="Close"
          onClick={() => !isSubmitting && onClose()}
          disabled={isSubmitting}
          size="small"
          sx={{ color: '#5a6478' }}
        >
          <CloseRoundedIcon />
        </IconButton>
      </CropModalTitle>

      <CropModalContent>
        <HintText>{BUSINESS_LOGO_DISPLAY_HINT}</HintText>

        {imageSrc ? (
          <>
            <CropStage>
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={BUSINESS_LOGO_CROP_ASPECT}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                objectFit="contain"
                showGrid={false}
              />
            </CropStage>

            <ZoomRow>
              <Typography variant="caption" sx={{ color: '#64748b', minWidth: 36 }}>
                Zoom
              </Typography>
              <Slider
                size="small"
                min={1}
                max={3}
                step={0.02}
                value={zoom}
                onChange={(_, v) => setZoom(v)}
                sx={{
                  flex: 1,
                  color: '#0d4efe',
                  '& .MuiSlider-thumb': {
                    width: 14,
                    height: 14,
                  },
                }}
              />
            </ZoomRow>

            <PreviewRow>
              <PreviewThumb>
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Square preview"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : null}
              </PreviewThumb>
              <PreviewCaption>
                Square preview — matches how your logo is cropped on listings and service cards.
              </PreviewCaption>
            </PreviewRow>
          </>
        ) : (
          <Typography color="text.secondary">No image loaded.</Typography>
        )}
      </CropModalContent>

      <CropModalActions>
        <SecondaryModalButton
          variant="outlined"
          onClick={() => !isSubmitting && onClose()}
          disabled={isSubmitting}
        >
          Cancel
        </SecondaryModalButton>
        <PrimaryModalButton variant="contained" disableElevation onClick={handleSave} disabled={!canSave}>
          {isSubmitting ? 'Saving…' : 'Save logo'}
        </PrimaryModalButton>
      </CropModalActions>
    </StyledCropDialog>
  );
}

BusinessLogoCropModal.propTypes = {
  open: PropTypes.bool.isRequired,
  imageSrc: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onCroppedConfirm: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
};

BusinessLogoCropModal.defaultProps = {
  imageSrc: null,
  isSubmitting: false,
};

export default BusinessLogoCropModal;
